import React from "react";
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  FormatSize as FormatSizeIcon,
  Padding as PaddingIcon,
  BlurOn as ShadowIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";
import DefaultLogo from "../../../src/assets/avatar.png";

const ComponentTools = ({ component, onUpdate }) => {
  if (!component) return null;

  const handleFieldChange = (field, value) => {
    const updates = { [field]: value };

    // Ajustar tamaño automáticamente según el contenido
    if (field === "options" && Array.isArray(value)) {
      const baseHeight = 10; // altura base en porcentaje
      const heightPerOption = 3; // altura adicional por opción
      const newHeight = Math.min(baseHeight + value.length * heightPerOption, 40); // máximo 40%
      updates.rndPosition = {
        ...component.rndPosition,
        height: newHeight,
      };
    }

    // Ajustar ancho para textos largos
    if (field === "label" || field === "title" || field === "text") {
      const textLength = value.length;
      let newWidth = component.rndPosition?.width || 40;

      if (textLength > 50) {
        newWidth = Math.min(80, 40 + (textLength - 50) * 0.5); // aumentar ancho gradualmente
      } else if (textLength > 20) {
        newWidth = Math.min(60, 40 + (textLength - 20) * 0.3);
      }

      if (newWidth !== component.rndPosition?.width) {
        updates.rndPosition = {
          ...component.rndPosition,
          width: newWidth,
        };
      }
    }

    onUpdate(updates);
  };

  const handleStyleChange = (stylePath, value) => {
    const currentStyle = component.style || {};
    const newStyle = { ...currentStyle };

    // Navegar por el path del estilo (ej: 'textStyle.color', 'padding.top')
    const pathParts = stylePath.split(".");
    let current = newStyle;

    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }

    current[pathParts[pathParts.length - 1]] = value;

    onUpdate({ style: newStyle });
  };

  const renderBasicFields = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
          Propiedades básicas
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {component.title !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Título"
              value={component.title || ""}
              onChange={(e) => handleFieldChange("title", e.target.value)}
            />
          )}
          {component.label !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Etiqueta"
              value={component.label || ""}
              onChange={(e) => handleFieldChange("label", e.target.value)}
            />
          )}
          {component.text !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Texto"
              value={component.text || ""}
              onChange={(e) => handleFieldChange("text", e.target.value)}
            />
          )}
          {component.placeholder !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Placeholder"
              value={component.placeholder || ""}
              onChange={(e) => handleFieldChange("placeholder", e.target.value)}
            />
          )}
          {component.subtitle !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Subtítulo"
              value={component.subtitle || ""}
              onChange={(e) => handleFieldChange("subtitle", e.target.value)}
            />
          )}
          {component.description !== undefined && (
            <TextField
              fullWidth
              size="small"
              label="Descripción"
              value={component.description || ""}
              onChange={(e) => handleFieldChange("description", e.target.value)}
              multiline
              rows={2}
            />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderOptionsFields = () => {
    if (!component.options) return null;
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Opciones
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {component.options.map((option, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  label={`Opción ${index + 1}`}
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...component.options];
                    newOptions[index] = e.target.value;
                    handleFieldChange("options", newOptions);
                  }}
                  fullWidth
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const newOptions = component.options.filter((_, i) => i !== index);
                    handleFieldChange("options", newOptions);
                  }}
                  disabled={component.options.length <= 1}
                >
                  ×
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const newOptions = [...component.options, `Opción ${component.options.length + 1}`];
                handleFieldChange("options", newOptions);
              }}
            >
              Agregar opción
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderValidationFields = () => {
    if (component.required === undefined && component.maxLength === undefined) return null;
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Validación
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {component.required !== undefined && (
              <FormControlLabel
                control={
                  <Switch
                    checked={component.required || false}
                    onChange={(e) => handleFieldChange("required", e.target.checked)}
                  />
                }
                label="Campo requerido"
              />
            )}
            {component.maxLength !== undefined && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Longitud máxima"
                value={component.maxLength || ""}
                onChange={(e) => handleFieldChange("maxLength", parseInt(e.target.value) || undefined)}
                inputProps={{ min: 0 }}
              />
            )}
            {component.minLength !== undefined && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Longitud mínima"
                value={component.minLength || ""}
                onChange={(e) => handleFieldChange("minLength", parseInt(e.target.value) || undefined)}
                inputProps={{ min: 0 }}
              />
            )}
            {component.pattern !== undefined && (
              <TextField
                fullWidth
                size="small"
                label="Patrón (regex)"
                value={component.pattern || ""}
                onChange={(e) => handleFieldChange("pattern", e.target.value)}
                helperText="Expresión regular para validación"
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderInputSpecificFields = () => {
    if (component.type !== "input" && component.type !== "textArea") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Configuración de entrada
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {component.type === "input" && (
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de entrada</InputLabel>
                <Select
                  value={component.inputType || "text"}
                  onChange={(e) => handleFieldChange("inputType", e.target.value)}
                  label="Tipo de entrada"
                >
                  <MenuItem value="text">Texto</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="password">Contraseña</MenuItem>
                  <MenuItem value="number">Número</MenuItem>
                  <MenuItem value="tel">Teléfono</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                </Select>
              </FormControl>
            )}
            {component.type === "textArea" && (
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Filas"
                value={component.rows || 3}
                onChange={(e) => handleFieldChange("rows", parseInt(e.target.value))}
                inputProps={{ min: 1, max: 10 }}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSelectSpecificFields = () => {
    if (component.type !== "select") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Configuración del selector
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={component.multiple || false}
                  onChange={(e) => handleFieldChange("multiple", e.target.checked)}
                />
              }
              label="Selección múltiple"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={component.disabled || false}
                  onChange={(e) => handleFieldChange("disabled", e.target.checked)}
                />
              }
              label="Deshabilitado"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderButtonSpecificFields = () => {
    if (component.type !== "button") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Configuración del botón
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Acción</InputLabel>
              <Select
                value={component.action || "submit"}
                onChange={(e) => handleFieldChange("action", e.target.value)}
                label="Acción"
              >
                <MenuItem value="submit">Enviar formulario</MenuItem>
                <MenuItem value="reset">Limpiar formulario</MenuItem>
                <MenuItem value="custom">Acción personalizada</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Variante</InputLabel>
              <Select
                value={component.variant || "contained"}
                onChange={(e) => handleFieldChange("variant", e.target.value)}
                label="Variante"
              >
                <MenuItem value="contained">Contenido</MenuItem>
                <MenuItem value="outlined">Borde</MenuItem>
                <MenuItem value="text">Texto</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Tamaño</InputLabel>
              <Select
                value={component.size || "medium"}
                onChange={(e) => handleFieldChange("size", e.target.value)}
                label="Tamaño"
              >
                <MenuItem value="small">Pequeño</MenuItem>
                <MenuItem value="medium">Mediano</MenuItem>
                <MenuItem value="large">Grande</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={component.disabled || false}
                  onChange={(e) => handleFieldChange("disabled", e.target.checked)}
                />
              }
              label="Deshabilitado"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={component.fullWidth || false}
                  onChange={(e) => handleFieldChange("fullWidth", e.target.checked)}
                />
              }
              label="Ancho completo"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderImageSpecificFields = () => {
    if (component.type !== "image") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Configuración de imagen
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Texto alternativo"
              value={component.alt || ""}
              onChange={(e) => handleFieldChange("alt", e.target.value)}
              helperText="Descripción de la imagen para accesibilidad"
            />
            <TextField
              fullWidth
              size="small"
              label="Placeholder"
              value={component.placeholder || ""}
              onChange={(e) => handleFieldChange("placeholder", e.target.value)}
              helperText="Texto a mostrar cuando no hay imagen"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Ajuste de imagen</InputLabel>
              <Select
                value={component.objectFit || "contain"}
                onChange={(e) => handleFieldChange("objectFit", e.target.value)}
                label="Ajuste de imagen"
              >
                <MenuItem value="contain">Contener</MenuItem>
                <MenuItem value="cover">Cubrir</MenuItem>
                <MenuItem value="fill">Llenar</MenuItem>
                <MenuItem value="scale-down">Escalar hacia abajo</MenuItem>
                <MenuItem value="none">Ninguno</MenuItem>
              </Select>
            </FormControl>
            {component.src && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Vista previa:
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 1,
                    display: "flex",
                    justifyContent: "center",
                    maxHeight: 100,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={component.src}
                    alt={component.alt || "Vista previa"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => component.onImageClick && component.onImageClick()}
              sx={{ mt: 1 }}
            >
              {component.src ? "Cambiar imagen" : "Cargar imagen"}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderDividerSpecificFields = () => {
    if (component.type !== "divider") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Configuración del separador
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Orientación</InputLabel>
              <Select
                value={component.orientation || "horizontal"}
                onChange={(e) => handleFieldChange("orientation", e.target.value)}
                label="Orientación"
              >
                <MenuItem value="horizontal">Horizontal</MenuItem>
                <MenuItem value="vertical">Vertical</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Variante</InputLabel>
              <Select
                value={component.variant || "fullWidth"}
                onChange={(e) => handleFieldChange("variant", e.target.value)}
                label="Variante"
              >
                <MenuItem value="fullWidth">Ancho completo</MenuItem>
                <MenuItem value="inset">Inset</MenuItem>
                <MenuItem value="middle">Centro</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Grosor (px)"
              value={component.thickness || 1}
              onChange={(e) => handleFieldChange("thickness", parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 10 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderCellsSpecificFields = () => {
    if (component.type !== "cells") return null;

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Estilos de celdas
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Fondo de celda */}
            <TextField
              fullWidth
              size="small"
              label="Color de fondo de celda"
              value={component.style?.backgroundColor || "#ffffff"}
              onChange={(e) => handleStyleChange("backgroundColor", e.target.value)}
              type="color"
            />
            {/* Estilo de texto */}
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Tamaño de fuente"
              value={component.style?.textStyle?.fontSize || 14}
              onChange={(e) => handleStyleChange("textStyle.fontSize", parseInt(e.target.value) || 14)}
              inputProps={{ min: 8, max: 72 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Peso de fuente</InputLabel>
              <Select
                value={component.style?.textStyle?.fontWeight || "normal"}
                onChange={(e) => handleStyleChange("textStyle.fontWeight", e.target.value)}
                label="Peso de fuente"
              >
                <MenuItem value="light">Ligero</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="medium">Medio</MenuItem>
                <MenuItem value="bold">Negrita</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Color del texto"
              value={component.style?.textStyle?.color || "#000000"}
              onChange={(e) => handleStyleChange("textStyle.color", e.target.value)}
              type="color"
            />
            {/* Alineación centrada (fija) */}
            <Typography variant="caption" color="text.secondary">
              Las celdas estarán alineadas al centro.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderTextStyleFields = () => {
    // Si es etiqueta, quitar alineación
    const isLabel = component.type === "label";
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormatSizeIcon sx={{ fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
              Estilo de texto
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Tamaño de fuente"
              value={component.style?.textStyle?.fontSize || 14}
              onChange={(e) => handleStyleChange("textStyle.fontSize", parseInt(e.target.value) || 14)}
              inputProps={{ min: 8, max: 72 }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Peso de fuente</InputLabel>
              <Select
                value={component.style?.textStyle?.fontWeight || "normal"}
                onChange={(e) => handleStyleChange("textStyle.fontWeight", e.target.value)}
                label="Peso de fuente"
              >
                <MenuItem value="light">Ligero</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="medium">Medio</MenuItem>
                <MenuItem value="bold">Negrita</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Color del texto"
              value={component.style?.textStyle?.color || "#000000"}
              onChange={(e) => handleStyleChange("textStyle.color", e.target.value)}
              type="color"
            />
            {/* Solo mostrar alineación si no es etiqueta */}
            {!isLabel && (
              <FormControl fullWidth size="small">
                <InputLabel>Alineación</InputLabel>
                <Select
                  value={component.style?.textStyle?.textAlign || "left"}
                  onChange={(e) => handleStyleChange("textStyle.textAlign", e.target.value)}
                  label="Alineación"
                >
                  <MenuItem value="left">Izquierda</MenuItem>
                  <MenuItem value="center">Centro</MenuItem>
                  <MenuItem value="right">Derecha</MenuItem>
                  <MenuItem value="justify">Justificado</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBackgroundFields = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaletteIcon sx={{ fontSize: 16 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Fondo
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Color de fondo"
            value={component.style?.backgroundColor || "#ffffff"}
            onChange={(e) => handleStyleChange("backgroundColor", e.target.value)}
            type="color"
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderSpacingFields = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaddingIcon sx={{ fontSize: 16 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Espaciado
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="caption">Padding</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Arriba"
                value={component.style?.padding?.top || 0}
                onChange={(e) => handleStyleChange("padding.top", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Abajo"
                value={component.style?.padding?.bottom || 0}
                onChange={(e) => handleStyleChange("padding.bottom", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Izquierda"
                value={component.style?.padding?.left || 0}
                onChange={(e) => handleStyleChange("padding.left", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Derecha"
                value={component.style?.padding?.right || 0}
                onChange={(e) => handleStyleChange("padding.right", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption">Margin</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Arriba"
                value={component.style?.margin?.top || 0}
                onChange={(e) => handleStyleChange("margin.top", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Abajo"
                value={component.style?.margin?.bottom || 0}
                onChange={(e) => handleStyleChange("margin.bottom", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Izquierda"
                value={component.style?.margin?.left || 0}
                onChange={(e) => handleStyleChange("margin.left", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Derecha"
                value={component.style?.margin?.right || 0}
                onChange={(e) => handleStyleChange("margin.right", parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderShadowFields = () => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShadowIcon sx={{ fontSize: 16 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Sombra
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={component.style?.shadow?.enabled || false}
                onChange={(e) => handleStyleChange("shadow.enabled", e.target.checked)}
              />
            }
            label="Habilitar sombra"
          />
          {component.style?.shadow?.enabled && (
            <>
              <TextField
                fullWidth
                size="small"
                label="Color de sombra"
                value={component.style?.shadow?.color || "#000000"}
                onChange={(e) => handleStyleChange("shadow.color", e.target.value)}
                type="color"
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Desenfoque"
                value={component.style?.shadow?.blur || 4}
                onChange={(e) => handleStyleChange("shadow.blur", parseInt(e.target.value) || 4)}
                inputProps={{ min: 0, max: 50 }}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Offset X"
                value={component.style?.shadow?.offsetX || 0}
                onChange={(e) => handleStyleChange("shadow.offsetX", parseInt(e.target.value) || 0)}
                inputProps={{ min: -50, max: 50 }}
              />
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Offset Y"
                value={component.style?.shadow?.offsetY || 2}
                onChange={(e) => handleStyleChange("shadow.offsetY", parseInt(e.target.value) || 2)}
                inputProps={{ min: -50, max: 50 }}
              />
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  const renderFormHeaderFields = () => {
    if (component.type !== "formHeader") return null;
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: "medium" }}>
            Propiedades de cabecera
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del logo"
              value={
                component.nameLogo !== undefined && component.nameLogo !== ""
                  ? component.nameLogo
                  : "Nombre del hospital"
              }
              onChange={(e) => handleFieldChange("nameLogo", e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Título"
              value={
                component.titleName !== undefined && component.titleName !== ""
                  ? component.titleName
                  : "Nuevo Formulario"
              }
              onChange={(e) => handleFieldChange("titleName", e.target.value)}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={component.logo || DefaultLogo}
                alt="Logo preview"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 8,
                  border: "1px solid #e0e0e0",
                  objectFit: "cover",
                  background: "#fff",
                }}
              />
              <Button variant="outlined" component="label" size="small">
                Cambiar logo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        handleFieldChange("logo", ev.target.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const handleAutoResize = () => {
    let newWidth = component.rndPosition?.width || 40;
    let newHeight = component.rndPosition?.height || 10;

    // Ajustar según el tipo de componente y su contenido
    switch (component.type) {
      case "checklist":
      case "radiobutton":
        if (component.options && component.options.length > 0) {
          newHeight = Math.min(10 + component.options.length * 3, 40);
        }
        break;
      case "textArea":
        if (component.rows) {
          newHeight = Math.min(10 + component.rows * 2, 30);
        }
        break;
      case "header":
        if (component.title && component.title.length > 30) {
          newWidth = Math.min(80, 60 + (component.title.length - 30) * 0.3);
        }
        break;
      case "input":
      case "select":
        if (component.label && component.label.length > 20) {
          newWidth = Math.min(70, 50 + (component.label.length - 20) * 0.4);
        }
        break;
      case "cells":
        if (component.rows && component.columns) {
          newHeight = Math.min(25 + component.rows * 2, 50);
          newWidth = Math.min(70 + component.columns * 5, 90);
        }
        break;
    }

    onUpdate({
      rndPosition: {
        ...component.rndPosition,
        width: newWidth,
        height: newHeight,
      },
    });
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Header con tipo de componente */}
      <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ textTransform: "capitalize" }}>
            {component.type}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {component.id}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Campos específicos de formHeader */}
      {renderFormHeaderFields()}

      {/* Campos básicos */}
      {renderBasicFields()}

      {/* Opciones para componentes que las tienen */}
      {renderOptionsFields()}

      {/* Validación */}
      {renderValidationFields()}

      {/* Campos específicos por tipo */}
      {renderInputSpecificFields()}
      {renderSelectSpecificFields()}
      {renderButtonSpecificFields()}
      {renderImageSpecificFields()}
      {renderDividerSpecificFields()}
      {renderCellsSpecificFields()}

      {/* Estilos */}
      {renderTextStyleFields()}
      {renderBackgroundFields()}
      {renderSpacingFields()}
      {renderShadowFields()}
    </Box>
  );
};

export default ComponentTools;
